/**
 * RF-SLC20260926-004 — Vista de controlo de emissão de faturas automáticas (UI-04-01).
 *
 * Modelada no componente de referência `codebase/client/src/modules/pagamentos/list`:
 *   - Paginação/ordenação/filtro **server-side** via `CoreFaturacaoAutomatica.table({ limit, page, filter, sortBy })`
 *     (retorna `PaginatedDocumented`), com a config de filtros vinda de `CoreFaturacaoAutomatica.options()`.
 *   - Operadores MRT traduzidos para a DSL do servidor por `getType()` (`$ilike`, `$sw`, `$eq`, …).
 *   - Padrão `initialFetch`/`fetchData`/`refetching` + `useEffect([pagination, filter, sort])`.
 *   - Estilo de tabela: `Breadcrumbs`, `Cell` a ler o valor cru de `row.original` (evita o highlight do MRT que, com filtro ativo, devolve um nó React e produziria "[object Object]"), `<Typography>`, `Intl.NumberFormat` para moeda, datas em `DD/MM/YYYY`.
 *
 * Ações por linha (UI-04-01): Ver detalhes (UI-04-02), Tentar novamente
 * (`POST /:id/tentar-novamente`, único método remoto custom) e Transferir
 * (UI-04-03 — URL de download do storage construído a partir de `ficheiro_uuid`).
 *
 * Registo/routing no módulo-pai `financeiro` (`index.js` + `financeiro.routes.js`);
 * props injetadas: `['CoreFaturacaoAutomatica', 'UIService']`.
 *
 * Spec: specs/01_requirements/RF-SLC20260926-004-vista-controlo.md
 * UI:   specs/04_ux_ui/web/ui-04-01-vista-controlo.md
 */
import React, { useEffect, useMemo, useState } from 'react';
import { Box, Breadcrumbs, Chip, IconButton, Link, Tooltip, Typography } from '@mui/material';
import { mdiDownload, mdiEye, mdiPencil, mdiRefresh } from '@mdi/js';
import moment from 'moment';
import { useModal } from 'mui-modal-provider';
import { MdiIcon, Table, WaitDialog } from '@components';
import DetailsDialog from './DetailsDialog';
// ── Constantes ────────────────────────────────────────────────────────────────
const ESTADO_ERRO_DEFINITIVO = 3;
const ESTADO_CONCLUIDO = 4;
// Cores de estado conforme UI-04-04.
const CORES_ESTADO = {
    1: { fundo: '#dee0e3', texto: '#807272', label: 'Aguarda Processamento' },
    2: { fundo: '#fff5cc', texto: '#FFA600', label: 'Em Processamento' },
    3: { fundo: '#fceaea', texto: '#DC2626', label: 'Erro definitivo' },
    4: { fundo: '#e6f9eb', texto: '#00BF33', label: 'Concluído' }
};
// Extensão (DEP) da pasta de PDFs por departamento — espelha
// `DEP_POR_DEPARTAMENTO` em `codebase/server/boot/faturacao_automatica.js`.
const DEP_POR_DEPARTAMENTO = { 1: 'ie', 2: 'for', 6: 'gas' };
// Opções dos filtros SELECT (departamento/estado). Departamentos no âmbito da
// faturação automática (RF-SLC20260926-003 §"Regras de negócio") e estados fixos (RF-SLC20260926-002).
// Hard-coded porque o componente só recebe `['CoreFaturacaoAutomatica', 'UIService']`
// — carregar via modelos exigiria injeções extra no `financeiro/index.js` (ver OQ-SLC20260926-033).
const DEPARTAMENTOS_OPCOES = [
    { value: 1, label: 'Inspeções Elétricas' },
    { value: 2, label: 'Formação' },
    { value: 6, label: 'Gás' }
];
const ESTADOS_OPCOES = [
    { value: 1, label: 'Aguarda Processamento' },
    { value: 2, label: 'Em Processamento' },
    { value: 3, label: 'Erro definitivo' },
    { value: 4, label: 'Concluído' }
];
// ── Helpers de apresentação (regras UI-04-01 §"Validações") ───────────────────
/** Texto vazio → "-". */
const txt = (v) => {
    if (v === null || v === undefined || ('' + v).trim() === '')
        return '-';
    return '' + v;
};
/** Data (só dia) vazia/invalida → "--/--/----". `data_registo` é gravado sem hora. */
const dataDia = (v) => {
    if (!v)
        return '--/--/----';
    const m = moment(v);
    return m.isValid() ? m.format('DD/MM/YYYY') : '--/--/----';
};
/** Soma do total das linhas. */
const totalPedido = (linhas) => (linhas || []).reduce((s, l) => s + (Number(l.itemQuantidade) || 0) * (Number(l.itemPrecoNet) || 0), 0);
const moeda = (v) => new Intl.NumberFormat('pt-PT', { style: 'currency', currency: 'EUR' }).format(v);
// ── Componente principal ─────────────────────────────────────────────────────
const FinanceiroFaturacaoAutomaticaListView = ({ CoreFaturacaoAutomatica, UIService }) => {
    // `WaitDialog` durante a exportação (UI-04-05), à semelhança de `pagamentos/list`.
    const { showModal } = useModal();
    const [data, setData] = useState();
    const [loading, setLoading] = useState(true);
    const [refetching, setRefetching] = useState(false);
    const [options, setOptions] = useState({ filter: [], sort: [] });
    const [pagination, setPagination] = useState({ pageIndex: 0, pageSize: 20 });
    const [filter, setFilter] = useState([]);
    const [sort, setSort] = useState([]);
    const [selecionado, setSelecionado] = useState(null);
    // Traduz o operador do MRT para a DSL do método `table` do servidor.
    const getType = (type) => {
        switch (type) {
            case 'contains':
                return '$ilike';
            case 'startsWith':
                return '$sw';
            case 'notEquals':
                return '$not';
            case 'empty':
                return '$null';
            case 'notEmpty':
                return '$notNull';
            case 'greaterThan':
                return '$gt';
            case 'greaterThanOrEqualTo':
                return '$gte';
            case 'lessThan':
                return '$lt';
            case 'lessThanOrEqualTo':
                return '$lte';
            default:
                return '$eq';
        }
    };
    const initialFetch = async () => {
        if (loading) {
            setLoading(false);
            try {
                const opt = await CoreFaturacaoAutomatica.options().$promise;
                setOptions(opt);
            }
            catch (e) {
                // `options()` pode não estar disponível — mantém o default.
            }
        }
    };
    const fetchData = async () => {
        try {
            const filters = {};
            filter.forEach(r => {
                filters[r.id] = `${getType(r.type)}:${r.value}`;
            });
            const sorter = [];
            sort.forEach(r => {
                sorter.push(`${r.id}:${r.desc ? 'DESC' : 'ASC'}`);
            });
            const aux = await CoreFaturacaoAutomatica.table({
                limit: pagination.pageSize,
                page: pagination.pageIndex + 1,
                filter: filters,
                sortBy: sorter
            }).$promise;
            setData(aux);
        }
        catch (e) {
            UIService.addToast('Erro a carregar pedidos: ' + (e?.data?.error?.message || e?.message || ''));
        }
        setRefetching(false);
    };
    useEffect(() => {
        initialFetch();
        if (!refetching) {
            setRefetching(true);
            fetchData();
        }
    }, [pagination, filter, sort]);
    // ── Ações ──────────────────────────────────────────────────────────────────
    const onVerDetalhes = async (pedido) => {
        // O `table()` não devolve a relação hasMany `linhas`; carrega-se o pedido
        // completo (com linhas + relações) por `findById` para a modal de detalhe.
        try {
            const completo = await CoreFaturacaoAutomatica.findById({
                id: pedido.id,
                filter: { include: ['departamento', 'funcionario', 'estado', 'linhas'] }
            }).$promise;
            setSelecionado((completo || pedido));
        }
        catch (e) {
            // Fallback: abre com os dados da linha (sem o detalhe das linhas de fatura).
            setSelecionado(pedido);
            UIService.addToast('Não foi possível carregar as linhas da fatura: ' + (e?.data?.error?.message || e?.message || ''));
        }
    };
    const onTentarNovamente = async (pedido) => {
        try {
            await CoreFaturacaoAutomatica.tentarNovamente({ id: pedido.id }).$promise;
            UIService.addToast(`Pedido #${pedido.id} recolocado em queue.`);
            setRefetching(true);
            await fetchData();
        }
        catch (e) {
            UIService.addToast('Erro ao tentar novamente: ' + (e?.data?.error?.message || e?.message || ''));
        }
    };
    // A7 — guarda os campos de faturação editados e repõe o pedido na queue (ação
    // atómica no servidor: `editarEReprocessar`). Fecha a modal e recarrega.
    const onGuardarEReprocessar = async (pedido, dados) => {
        try {
            await CoreFaturacaoAutomatica.editarEReprocessar({ id: pedido.id, dados }).$promise;
            UIService.addToast(`Pedido #${pedido.id} atualizado e recolocado em queue.`);
            setSelecionado(null);
            setRefetching(true);
            await fetchData();
        }
        catch (e) {
            UIService.addToast('Erro ao guardar e reprocessar: ' + (e?.data?.error?.message || e?.message || ''));
        }
    };
    const onTransferir = (pedido) => {
        // UI-04-03: PDF servido pelo storage (loopback-component-storage) em
        // `/api/Upload/faturas_automaticas_<DEP>/download/<ficheiro_uuid>.pdf`.
        const dep = pedido.departamentoId != null ? DEP_POR_DEPARTAMENTO[pedido.departamentoId] : undefined;
        if (!dep || !pedido.ficheiroUuid) {
            UIService.addToast('Fatura sem PDF disponível para transferência.');
            return;
        }
        const url = `/api/Upload/faturas_automaticas_${dep}/download/${pedido.ficheiroUuid}.pdf`;
        window.open(url, '_blank');
        UIService.addToast(`A transferir fatura do pedido #${pedido.id}.`);
    };
    // ── Colunas (UI-04-01) ───────────────────────────────────────────────────────
    // Sort/filtro server-side por coluna própria/FK (`*_id`); a designação é
    // renderizada no `Cell` a partir da relação incluída pelo `table()`.
    const columns = useMemo(() => [
        {
            accessorKey: 'departamentoId',
            exportKey: 'departamento.designacao',
            header: 'Departamento',
            size: 160,
            filterSelectOptions: DEPARTAMENTOS_OPCOES,
            Cell: ({ row }) => <Typography>{txt(row.original?.departamento?.designacao)}</Typography>
        },
        {
            accessorKey: 'numProcesso',
            header: 'Nº Processo',
            size: 140,
            // Usar o valor cru (`row.original`) e não `renderedCellValue`: com filtro
            // ativo o MRT passa um nó React (highlight) em `renderedCellValue` e o
            // `txt()` converteria para "[object Object]".
            Cell: ({ row }) => <Typography>{txt(row.original?.numProcesso)}</Typography>
        },
        {
            accessorKey: 'referencia',
            header: 'Referência',
            size: 140,
            // Referência de pagamento: preenchida em IE (Pagamento) e Formação
            // (CorePagamento); Gás não regista referência → mostra `-`.
            Cell: ({ row }) => <Typography>{txt(row.original?.referencia)}</Typography>
        },
        {
            accessorKey: 'nomeCliente',
            header: 'Cliente',
            size: 220,
            Cell: ({ row }) => <Typography>{txt(row.original?.nomeCliente)}</Typography>
        },
        {
            accessorKey: 'nif',
            header: 'NIF',
            size: 120,
            // Campo numérico → alinhado à direita.
            muiTableBodyCellProps: { align: 'right' },
            Cell: ({ row }) => <Typography>{txt(row.original?.nif)}</Typography>
        },
        {
            accessorKey: 'precoTotal',
            header: 'Preço Total',
            size: 120,
            // Computada (soma da relação hasMany `linhas`, pós-calculada no `table()`
            // do modelo). Não é filtrável/ordenável server-side via `paginate` — ver OQ-SLC20260926-033.
            enableColumnFilter: false,
            enableSorting: false,
            // Campo numérico → alinhado à direita.
            muiTableBodyCellProps: { align: 'right' },
            Cell: ({ row }) => {
                const t = row.original?.precoTotal != null ? Number(row.original.precoTotal) : totalPedido(row.original?.linhas);
                return <Typography>{t > 0 ? moeda(t) : '-'}</Typography>;
            }
        },
        {
            // Filtro (TEXT) e ordenação por nome do funcionário via JOIN à relação
            // `funcionario` (suportado por `paginate`).
            accessorKey: 'funcionario.name',
            header: 'Colaborador',
            size: 180,
            Cell: ({ row }) => <Typography>{txt(row.original?.funcionario?.name)}</Typography>
        },
        {
            accessorKey: 'dataRegisto',
            header: 'Data do Pedido',
            size: 160,
            // Data → alinhada à direita.
            muiTableBodyCellProps: { align: 'right' },
            Cell: ({ row }) => <Typography>{dataDia(row.original?.dataRegisto)}</Typography>
        },
        {
            accessorKey: 'idDocumentoErp',
            header: 'ID Fatura ERP',
            size: 140,
            visibility: false,
            // Inexistência → em dash (—), por o documento ainda não ter sido emitido no ERP.
            Cell: ({ row }) => <Typography>{row.original?.idDocumentoErp ? row.original.idDocumentoErp : '—'}</Typography>
        },
        {
            accessorKey: 'documentoErp',
            header: 'Nº Fatura',
            size: 140,
            // Inexistência → em dash (—), por o documento ainda não ter sido emitido no ERP.
            Cell: ({ row }) => <Typography>{row.original?.documentoErp ? row.original.documentoErp : '—'}</Typography>
        },
        {
            accessorKey: 'dataFaturacao',
            header: 'Data Faturação',
            size: 160,
            enableColumnFilter: false,
            enableSorting: false,
            // Data → alinhada à direita.
            muiTableBodyCellProps: { align: 'right' },
            // Data/hora da emissão da fatura (data_criacao, preenchida pelo worker).
            // Inexistência de data de faturação → em dash (—).
            Cell: ({ row }) => <Typography>{row.original?.estadoId === ESTADO_CONCLUIDO && row.original?.dataCriacao ? dataDia(row.original.dataCriacao) : '—'}</Typography>
        },
        {
            accessorKey: 'estadoId',
            exportKey: 'estado.designacao',
            header: 'Estado',
            size: 180,
            filterSelectOptions: ESTADOS_OPCOES,
            Cell: ({ row }) => {
                const e = row.original?.estadoId;
                const c = e ? CORES_ESTADO[e] : null;
                if (!c)
                    return <Typography>{txt(row.original?.estado?.designacao)}</Typography>;
                return <Chip size='small' label={c.label} sx={{ backgroundColor: c.fundo, color: c.texto, fontWeight: 500 }}/>;
            }
        }
    ], []);
    // ── Exportação (UI-04-05) ────────────────────────────────────────────────────
    /**
     * Exporta a listagem para Excel, modelado em `gas/processos/list`.
     *
     * Envia os filtros/ordenação ativos na mesma DSL do `fetchData`, pelo que o
     * ficheiro cobre **todos** os registos correspondentes e não apenas a página
     * visível. As colunas seguem a ordem e os cabeçalhos da tabela; `exportKey`
     * (quando definido) faz exportar a designação da relação em vez do id.
     *
     * O servidor devolve o `.xlsx` em base64 (`$data`) — descodificado aqui para
     * `Blob` e transferido. As colunas computadas (Preço Total, Data Faturação)
     * são preenchidas no `export()` do modelo.
     */
    const onExportar = async () => {
        const wait = showModal(WaitDialog, { title: 'Por favor aguarde...' });
        try {
            const filters = {};
            filter.forEach(r => {
                filters[r.id] = `${getType(r.type)}:${r.value}`;
            });
            const sorter = [];
            sort.forEach(r => {
                sorter.push(`${r.id}:${r.desc ? 'DESC' : 'ASC'}`);
            });
            const o = await CoreFaturacaoAutomatica.export({
                columns: columns.map(r => ({
                    accessorKey: r.exportKey ?? r.accessorKey,
                    header: r.header
                })),
                filter: filters,
                sortBy: sorter
            }).$promise;
            if (o) {
                // Descodifica o base64 e converte para Blob.
                const binary = atob(o.$data);
                const bytes = new Uint8Array(binary.length);
                for (let i = 0; i < binary.length; i++) {
                    bytes[i] = binary.charCodeAt(i);
                }
                const blob = new Blob([bytes], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
                const link = document.createElement('a');
                link.href = URL.createObjectURL(blob);
                link.download = `faturacao-automatica-exportacao-${moment().format('YYYY-MM-DD-HH-mm-ss')}.xlsx`;
                link.click();
            }
        }
        catch (e) {
            UIService.addToast(e?.data?.error?.message || e?.data?.message || 'Ocorreu um erro ao exportar dados!');
        }
        wait.destroy();
    };
    // ── Render ─────────────────────────────────────────────────────────────────
    return (<Box>
      <Box sx={{ mt: 2, mb: 2 }}>
        <Typography variant='h5'>Faturação Automática</Typography>
        <Breadcrumbs aria-label='breadcrumb'>
          <Link underline='none' color='inherit'>
            Financeiro
          </Link>
          <Link underline='hover' color='text.primary' aria-current='page'>
            Faturação Automática
          </Link>
        </Breadcrumbs>
      </Box>

      <Box>
        <Table storageKey='app.financeiro.faturacaoautomatica' data={data} columns={columns} initial={{ sort, filter }} isLoading={refetching} options={options} onPaginate={setPagination} onFilter={setFilter} onSort={setSort} 
    // UI-04-05: botão "Exportar" na barra de ferramentas da tabela.
    onExport={onExportar} 
    // Duplo clique numa linha abre a mesma modal de detalhes que o ícone "Ver detalhes".
    onDoubleClick={(pedido) => onVerDetalhes(pedido)} actionItems={({ row }) => {
            const pedido = row.original;
            const podeRetentar = pedido.estadoId === ESTADO_ERRO_DEFINITIVO && !pedido.documentoErp;
            const podeTransferir = !!pedido.ficheiroUuid;
            return [
                // A7 — lápis (editar) só quando é editável/reprocessável (Erro definitivo
                // sem fatura emitida); nos restantes estados mantém-se o olho (ver).
                <Tooltip key='ver-editar' title={podeRetentar ? 'Editar fatura' : 'Ver detalhes'}>
                <IconButton size='small' onClick={() => onVerDetalhes(pedido)}>
                  <MdiIcon path={podeRetentar ? mdiPencil : mdiEye} size={1}/>
                </IconButton>
              </Tooltip>,
                <Tooltip key='retry' title={podeRetentar ? 'Tentar novamente' : 'Indisponível neste estado'}>
                <span>
                  <IconButton size='small' disabled={!podeRetentar} onClick={() => onTentarNovamente(pedido)}>
                    <MdiIcon path={mdiRefresh} size={1}/>
                  </IconButton>
                </span>
              </Tooltip>,
                <Tooltip key='dl' title={podeTransferir ? 'Transferir fatura' : 'Sem PDF disponível'}>
                <span>
                  <IconButton size='small' disabled={!podeTransferir} onClick={() => onTransferir(pedido)}>
                    <MdiIcon path={mdiDownload} size={1}/>
                  </IconButton>
                </span>
              </Tooltip>
            ];
        }}/>
      </Box>

      {/* UI-04-02 — Dialog de detalhes */}
      {selecionado && (<DetailsDialog pedido={selecionado} onClose={() => setSelecionado(null)} onTentarNovamente={async () => {
                await onTentarNovamente(selecionado);
                setSelecionado(null);
            }} onTransferir={() => onTransferir(selecionado)} 
        // A7 — modo de edição sempre ligado; a modal só permite editar quando o
        // pedido está em Erro definitivo sem fatura emitida (validação interna).
        podeEditar onGuardarEReprocessar={dados => onGuardarEReprocessar(selecionado, dados)}/>)}
    </Box>);
};
export default FinanceiroFaturacaoAutomaticaListView;
